import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useRecordContext } from "react-admin";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { ChipSize, ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { useAppTheme } from "@/hooks/useAppTheme";

export type StatusChipColor = "default" | "info" | "success" | "warning" | "error" | "primary" | "secondary" | "muted";

interface StatusChipProps {
  label: string;
  color?: StatusChipColor;
  size?: ChipSize;
}

/**
 * A minimal, modern status chip with subtle background colors.
 * Use this directly when you have the label and color available.
 * For React Admin record contexts, use StatusChipField instead.
 */
export const StatusChip = ({ label, color = ColorType.Default, size = UIComponentSize.Medium }: StatusChipProps) => {
  const { theme, isDarkMode } = useAppTheme();

  const sizeStyles =
    size === UIComponentSize.Small
      ? { px: 1, py: 0.25, fontSize: "0.6875rem" }
      : { px: 1.5, py: 0.5, fontSize: "0.75rem" };

  const getColors = () => {
    switch (color) {
      case ColorType.Primary:
        return {
          bg: alpha(theme.palette.primary.main, 0.12),
          text: isDarkMode ? theme.palette.primary.light : theme.palette.primary.dark,
        };
      case ColorType.Secondary:
        return {
          bg: alpha(theme.palette.secondary.main, 0.12),
          text: isDarkMode ? theme.palette.secondary.light : theme.palette.secondary.dark,
        };
      case ColorType.Info:
        return {
          bg: alpha(theme.palette.info.main, 0.12),
          text: isDarkMode ? theme.palette.info.light : theme.palette.info.dark,
        };
      case ColorType.Success:
        return {
          bg: alpha(theme.palette.success.main, 0.12),
          text: isDarkMode ? theme.palette.success.light : theme.palette.success.dark,
        };
      case ColorType.Warning:
        return {
          bg: alpha(theme.palette.warning.main, 0.15),
          text: isDarkMode ? theme.palette.warning.light : "#92400E",
        };
      case ColorType.Error:
        return {
          bg: alpha(theme.palette.error.main, 0.12),
          text: isDarkMode ? theme.palette.error.light : theme.palette.error.dark,
        };
      case "muted":
        // De-emphasized state (e.g. superseded rows). Subtle background, disabled text.
        return {
          bg: isDarkMode ? alpha(theme.palette.grey[500], 0.08) : alpha(theme.palette.grey[500], 0.08),
          text: theme.palette.text.disabled,
        };
      case ColorType.Default:
      default:
        return {
          bg: isDarkMode ? alpha(theme.palette.grey[500], 0.16) : theme.palette.grey[100],
          text: isDarkMode ? theme.palette.grey[300] : theme.palette.text.primary,
        };
    }
  };

  const colors = getColors();

  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        ...sizeStyles,
        borderRadius: 1,
        bgcolor: colors.bg,
        color: colors.text,
        fontWeight: 600,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "100%",
        lineHeight: 1.5,
      }}
    >
      {label}
    </Box>
  );
};

interface StatusChipFieldProps {
  /** The source field name from the record for the status value */
  source: string;
}

/**
 * React Admin field component that renders a StatusChip based on record context.
 * Extracts the value from the specified source field, formats it, and determines color.
 *
 * @param {StatusChipFieldProps} props - The component props
 * @returns A StatusChip displaying the formatted status with the corresponding color
 */
export const StatusChipField = ({ source }: StatusChipFieldProps) => {
  const record = useRecordContext();

  if (!record) return null;

  const value = record[source];
  const label = value ? (value === "processing" ? "Processing..." : formatToTitleCase(value)) : "Unknown";

  const color = getStatusColor(value) ?? ColorType.Default;

  return <StatusChip label={label} color={color} />;
};
